/**
 * Campaign Kanban Board
 *
 * Alternative view for the campaign overview: campaigns grouped into
 * lifecycle columns (Entwurf → Aktiv → Pausiert/Problem → Beendet) so the
 * state of all campaigns is visible at a glance — instead of decoding it
 * from the funnel table.
 */

import React, { useMemo } from 'react';
import { Box, Paper, Typography, Chip, Stack, Badge, Tooltip } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { FunnelOverview } from './campaign-overview.types';
import { FunnelStatuses } from '../co-pilot.types';

interface Props {
  funnels: FunnelOverview[];
  mapLinkedinAccountIdToName: (accountId: string) => string;
  riskReductionForAccount?: (accountId: string) => { until: Date; percent: number } | null;
  onOpenDetail: (funnel: FunnelOverview) => void;
}

type ColumnKey = 'draft' | 'active' | 'attention' | 'finished';

const ATTENTION_STATUSES = new Set<string>([
  FunnelStatuses.PAUSED,
  FunnelStatuses.SUSPENDED,
  FunnelStatuses.ACCOUNT_NOT_FOUND,
  FunnelStatuses.LINKEDIN_ACCOUNT_PROBLEM,
  FunnelStatuses.CO_PILOT_LICENSE_EXPIRED,
  FunnelStatuses.CONTACTS_ALMOST_EXHAUSTED,
]);

function columnOf(funnel: FunnelOverview): ColumnKey {
  if (funnel.status === FunnelStatuses.FINISHED || funnel.isCompleted) return 'finished';
  if (ATTENTION_STATUSES.has(funnel.status)) return 'attention';
  if (funnel.status === FunnelStatuses.STARTED) return 'active';
  return 'draft';
}

const CampaignKanban: React.FC<Props> = ({ funnels, mapLinkedinAccountIdToName, riskReductionForAccount, onOpenDetail }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const columns = useMemo(() => {
    const grouped: Record<ColumnKey, FunnelOverview[]> = { draft: [], active: [], attention: [], finished: [] };
    for (const funnel of funnels) {
      if (funnel.status === FunnelStatuses.ARCHIVED) continue;
      grouped[columnOf(funnel)].push(funnel);
    }
    return grouped;
  }, [funnels]);

  const columnDefs: Array<{ key: ColumnKey; title: string; icon: React.ReactNode; accent: string }> = [
    { key: 'draft', title: t('kanbanDraft', 'Entwurf'), icon: <EditNoteIcon fontSize="small" />, accent: 'draft' },
    { key: 'active', title: t('kanbanActive', 'Aktiv'), icon: <PlayArrowIcon fontSize="small" />, accent: 'active' },
    { key: 'attention', title: t('kanbanAttention', 'Pausiert / Achtung'), icon: <PauseCircleOutlineIcon fontSize="small" />, accent: 'attention' },
    { key: 'finished', title: t('kanbanFinished', 'Beendet'), icon: <CheckCircleOutlineIcon fontSize="small" />, accent: 'finished' },
  ];

  return (
    <Box className={classes.board}>
      {columnDefs.map((col) => (
        <Box key={col.key} className={classes.column}>
          <Stack direction="row" alignItems="center" gap={1} className={cx(classes.columnHeader, classes[col.accent as 'draft'])}>
            {col.icon}
            <Typography variant="subtitle2" className={classes.columnTitle}>
              {col.title}
            </Typography>
            <Chip size="small" label={columns[col.key].length} />
          </Stack>

          <Box className={classes.columnBody}>
            {columns[col.key].length === 0 && (
              <Typography variant="caption" color="textSecondary" className={classes.emptyHint}>
                {t('kanbanEmpty', '—')}
              </Typography>
            )}
            {columns[col.key].map((funnel) => {
              const summary = funnel.funnelSummary;
              const total = summary?.totalContactsCount ?? 0;
              const activeContacts = summary?.activeContactsCount ?? 0;
              const done = Math.max(0, total - activeContacts);
              const progress = total > 0 ? Math.round((done / total) * 100) : 0;
              const needsAttention = ATTENTION_STATUSES.has(funnel.status);
              return (
                <Paper
                  key={funnel._id}
                  className={classes.card}
                  elevation={0}
                  onClick={() => onOpenDetail(funnel)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onOpenDetail(funnel);
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" className={classes.cardTitle}>
                      {funnel.name}
                    </Typography>
                    {funnel.toBeApprovedMessages > 0 && (
                      <Tooltip title={t('kanbanToApprove', '{{count}} Nachrichten freizugeben', { count: funnel.toBeApprovedMessages })}>
                        <Badge badgeContent={funnel.toBeApprovedMessages} color="warning" max={99}>
                          <MarkChatUnreadIcon fontSize="small" color="action" />
                        </Badge>
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="caption" color="textSecondary" noWrap>
                    {mapLinkedinAccountIdToName(funnel.linkedinAccount)}
                  </Typography>
                  {(() => {
                    const rr = riskReductionForAccount?.(funnel.linkedinAccount);
                    if (!rr) return null;
                    const values = { date: rr.until.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }), percent: rr.percent };
                    return (
                      <Tooltip title={t('riskReductionHint', values)}>
                        <Chip size="small" color="warning" variant="outlined" label={t('riskReductionLabel', values)} />
                      </Tooltip>
                    );
                  })()}
                  {funnel.scheduledStartAt && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={t('kanbanScheduledFor', '📅 Geplant für {{date}}', {
                        date: new Date(funnel.scheduledStartAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                      })}
                      className={classes.scheduledChip}
                    />
                  )}
                  {total > 0 && (
                    <Box className={classes.progressRow}>
                      <Box className={classes.progressTrack}>
                        <Box className={classes.progressFill} style={{ width: `${progress}%` }} />
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {done}/{total}
                      </Typography>
                    </Box>
                  )}
                  {needsAttention && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <WarningAmberIcon color="warning" sx={{ fontSize: '0.9rem' }} />
                      <Typography variant="caption" color="warning.main">
                        {String(funnel.status).replace(/-/g, ' ')}
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  board: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'start',
    overflowX: 'auto',
    paddingBottom: theme.spacing(1),
  },
  column: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f6f7f9',
    borderRadius: '12px',
    padding: theme.spacing(1),
    minHeight: 200,
  },
  columnHeader: {
    padding: theme.spacing(0.5, 1),
    borderRadius: '8px',
    marginBottom: theme.spacing(1),
  },
  columnTitle: {
    fontWeight: 600,
    flexGrow: 1,
  },
  draft: { color: theme.palette.text.secondary },
  active: { color: theme.palette.success.main },
  attention: { color: theme.palette.warning.main },
  finished: { color: theme.palette.primary.main },
  columnBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  emptyHint: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(1, 1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['box-shadow', 'border-color'], { duration: 120 }),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[2],
    },
  },
  scheduledChip: {
    alignSelf: 'flex-start',
  },
  cardTitle: {
    fontWeight: 500,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  progressTrack: {
    flexGrow: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300],
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: theme.palette.success.main,
  },
}));

export default CampaignKanban;
